import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Users } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getEmployeesInvolved } from "../../stakeholder/logic/stakeholderSlice";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";
import { InvestigationEntityCard } from "./InvestigationEntityCard";

import EmployeeManageModal from "./EmployeeManageModal";
import EntityPopoverRowWithBadge from "./EntityPopoverRow";

const EmployeeCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();

  const [isManageModalOpen, setIsManageModalOpen] = useState(false);

  const employees = useAppSelector(getEmployeesInvolved);

  const employeeStakeholderIds = useMemo(
    () => employees.map((employee) => employee.stakeholderId),
    [employees]
  );

  useEffect(() => {
    if (!caseId || employeeStakeholderIds.length === 0) {
      return;
    }

    dispatch(
      fetchEnrichedStakeholder({
        caseId: caseId,
        stakeholderIds: employeeStakeholderIds,
      })
    );
  }, [caseId, dispatch, employeeStakeholderIds]);

  const employeeItems = useMemo(
    () =>
      employees.map(
        (employee) => `${employee.name} (${employee.employeeId})`
      ),
    [employees]
  );

  return (
    <>
      <InvestigationEntityCard
        title="Employees"
        items={employeeItems}
        onAdd={() => {}}
        onManage={() => setIsManageModalOpen(true)}
        disableAdd
        disableManage={false}
        disableAddMessage="Employees can be added from the Parties Involved section."
        popoverProps={{
          title: "Employee",
          icon: <Users size={16} className="text-[#94A3B8]" />,
          allowCopy: true,
          items: employees.map((employee, index) => ({
            content: (
              <EntityPopoverRowWithBadge
                title={employee.name}
                badge={employee.relatedAs}
                secondaryText={employee.employeeId}
                showDivider={index > 0}
              />
            ),
            copyValue: employee.employeeId,
          })),
        }}
        emptyState={{
          icon: <Users size={18} className="text-[#94A3B8]" />,
          message: "No employees added",
        }}
      />

      {isManageModalOpen && (
        <EmployeeManageModal
          onClose={() => setIsManageModalOpen(false)}
        />
      )}
    </>
  );
};

export default EmployeeCard;